import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { ArrowLeft, ChevronRight, GitMerge, Check, X } from "lucide-react";
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { useVendors } from "../../lib/vendors/useVendors";
import { useExpenses } from "../../lib/expenses/useExpenses";
import { useInvoices } from "../../lib/invoices/useInvoices";
import { useCapabilities } from "../../lib/permissions/useCapabilities";
import { formatAmountCents, formatIsoDate } from "../../lib/format";
import type { Vendor } from "../../lib/vendors/types";
import { VendorLogo } from "../components/VendorLogo";

// 6-month sparkline bucket by YYYY-MM.
function monthKey(iso: string): string | null {
  if (!iso || iso.length < 7) return null;
  return iso.slice(0, 7);
}

function last6MonthKeys(): string[] {
  const now = new Date();
  const keys: string[] = [];
  for (let i = 5; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    keys.push(`${y}-${m}`);
  }
  return keys;
}

export function VendorDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { vendors, update, merge } = useVendors();
  const { expenses } = useExpenses();
  const { invoices } = useInvoices();
  const { can } = useCapabilities();
  // Vendor edits (rename / merge) live behind write:vendors. Matches the
  // previous role check — admin + owner both get it via ROLE_DEFAULTS.
  const canEditVendor = can("write:vendors");

  const vendor = useMemo<Vendor | undefined>(
    () => vendors.find((v) => v.id === id),
    [vendors, id],
  );

  const linkedExpenses = useMemo(
    () => expenses.filter((e) => e.vendorId === id),
    [expenses, id],
  );
  const linkedInvoices = useMemo(
    () => invoices.filter((i) => i.vendorId === id),
    [invoices, id],
  );

  const totalCents = useMemo(() => {
    let sum = 0;
    for (const e of linkedExpenses) sum += e.amountCents;
    for (const i of linkedInvoices) sum += i.amountCents;
    return sum;
  }, [linkedExpenses, linkedInvoices]);

  // Last-6-months spend sparkline. We bucket expense.occurredOn and
  // invoice.issuedOn into YYYY-MM and sum, then fill the last 6 months so
  // the chart has zero-padded bars for quiet months.
  const spark = useMemo(() => {
    const bucket = new Map<string, number>();
    for (const e of linkedExpenses) {
      const k = monthKey(e.occurredOn);
      if (k) bucket.set(k, (bucket.get(k) ?? 0) + e.amountCents);
    }
    for (const i of linkedInvoices) {
      const k = monthKey(i.issuedOn);
      if (k) bucket.set(k, (bucket.get(k) ?? 0) + i.amountCents);
    }
    return last6MonthKeys().map((k) => ({
      month: k,
      label: k.slice(5),
      cents: bucket.get(k) ?? 0,
    }));
  }, [linkedExpenses, linkedInvoices]);

  // Edit-in-place for canonical name. Admin/owner only.
  const [editing, setEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [editError, setEditError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function startEdit() {
    if (!vendor) return;
    setNameDraft(vendor.canonicalName);
    setEditError(null);
    setEditing(true);
  }
  function cancelEdit() {
    setEditing(false);
    setEditError(null);
  }
  async function saveEdit() {
    if (!vendor) return;
    const trimmed = nameDraft.trim();
    if (!trimmed) {
      setEditError("Name is required.");
      return;
    }
    setSaving(true);
    setEditError(null);
    try {
      await update(vendor.id, { canonicalName: trimmed });
      setEditing(false);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  // Merge dialog state.
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeTargetId, setMergeTargetId] = useState("");
  const [mergeError, setMergeError] = useState<string | null>(null);
  const [merging, setMerging] = useState(false);

  const mergeCandidates = useMemo(
    () => vendors.filter((v) => v.id !== id),
    [vendors, id],
  );

  async function runMerge() {
    if (!vendor || !mergeTargetId) {
      setMergeError("Pick a target vendor.");
      return;
    }
    setMerging(true);
    setMergeError(null);
    try {
      await merge(vendor.id, mergeTargetId);
      setMergeOpen(false);
      // Redirect to the target so the user sees the combined view.
      navigate(`/vendors/${mergeTargetId}`);
    } catch (err) {
      setMergeError(err instanceof Error ? err.message : "Merge failed");
    } finally {
      setMerging(false);
    }
  }

  if (!vendor) {
    return (
      <div
        data-testid="vendor-detail-missing"
        className="max-w-4xl mx-auto py-10 text-center text-sm text-slate-500"
      >
        <Link to="/vendors" className="text-[#0070F2] hover:text-[#005BCA]">
          ← Back to Vendors
        </Link>
        <p className="mt-4">Vendor not found.</p>
      </div>
    );
  }

  return (
    <div
      data-testid="vendor-detail-page"
      className="flex flex-col gap-6 max-w-5xl mx-auto"
    >
      <div className="flex items-center text-sm text-slate-500 gap-1">
        <Link to="/vendors" className="hover:text-slate-900 inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Vendors
        </Link>
        <ChevronRight className="w-4 h-4" />
        <span className="text-slate-700">{vendor.canonicalName}</span>
      </div>

      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0 flex items-start gap-3">
          <VendorLogo vendor={vendor} size={48} />
          <div className="flex-1 min-w-0">
          {editing ? (
            <div className="flex items-center gap-2">
              <input
                data-testid="vendor-detail-name-input"
                type="text"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                className="px-3 py-1.5 text-lg font-semibold rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#0070F2]/30 focus:border-[#0070F2]"
              />
              <button
                type="button"
                data-testid="vendor-detail-name-save"
                onClick={() => void saveEdit()}
                disabled={saving}
                className="p-1.5 text-white bg-[#0070F2] hover:bg-[#005BCA] disabled:bg-slate-300 rounded-md"
                aria-label="Save"
              >
                <Check className="w-4 h-4" />
              </button>
              <button
                type="button"
                data-testid="vendor-detail-name-cancel"
                onClick={cancelEdit}
                className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md"
                aria-label="Cancel"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <h1
              data-testid="vendor-detail-name"
              className="text-2xl font-semibold text-slate-900"
            >
              {vendor.canonicalName}
            </h1>
          )}
          {editError && (
            <div
              data-testid="vendor-detail-name-error"
              className="mt-1 text-xs text-red-600"
            >
              {editError}
            </div>
          )}
          <div className="mt-1 text-sm text-slate-500">
            {linkedExpenses.length + linkedInvoices.length} transactions ·{" "}
            {formatAmountCents(totalCents)} total
          </div>
          </div>
        </div>
        {canEditVendor && !editing && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="vendor-detail-rename"
              onClick={startEdit}
              className="px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-md"
            >
              Rename
            </button>
            <button
              type="button"
              data-testid="vendor-detail-merge"
              onClick={() => {
                setMergeTargetId("");
                setMergeError(null);
                setMergeOpen(true);
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[#0070F2] hover:bg-[#005BCA] rounded-md"
            >
              <GitMerge className="w-3.5 h-3.5" /> Merge into…
            </button>
          </div>
        )}
      </div>

      <div
        data-testid="vendor-detail-spark"
        className="bg-white rounded-xl border border-slate-200 p-4"
      >
        <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
          Last 6 months
        </div>
        <div className="h-24">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={spark} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="vendor-spark" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0070F2" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#0070F2" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10, fill: "#64748b" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis hide />
              <Tooltip
                formatter={(value: number) => formatAmountCents(value)}
                labelFormatter={(l: string) => `Month ${l}`}
                contentStyle={{ fontSize: "12px" }}
              />
              <Area
                type="monotone"
                dataKey="cents"
                stroke="#0070F2"
                strokeWidth={2}
                fill="url(#vendor-spark)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <section
        data-testid="vendor-detail-expenses"
        className="bg-white rounded-xl border border-slate-200 overflow-hidden"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-slate-900">
            Linked expenses
          </h2>
          <span
            data-testid="vendor-detail-expenses-count"
            className="text-xs text-slate-500"
          >
            {linkedExpenses.length}
          </span>
        </div>
        {linkedExpenses.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-500">
            No expenses linked to this vendor yet.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-medium text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200">
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">Vendor (as entered)</th>
                <th className="px-4 py-2 text-right">Amount</th>
                <th className="px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {linkedExpenses.map((e) => (
                <tr
                  key={e.id}
                  data-testid={`vendor-detail-expense-row-${e.id}`}
                  className="border-b last:border-b-0 border-slate-100 hover:bg-slate-50"
                >
                  <td className="px-4 py-2.5 text-slate-600">
                    {formatIsoDate(e.occurredOn)}
                  </td>
                  <td className="px-4 py-2.5">
                    <Link
                      to={`/expenses/${e.id}`}
                      className="text-slate-900 hover:text-[#0070F2]"
                    >
                      {e.vendor}
                    </Link>
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums">
                    {formatAmountCents(e.amountCents, e.currency)}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-500 capitalize">
                    {e.status.replace(/_/g, " ")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section
        data-testid="vendor-detail-invoices"
        className="bg-white rounded-xl border border-slate-200 overflow-hidden"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-slate-900">
            Linked invoices
          </h2>
          <span
            data-testid="vendor-detail-invoices-count"
            className="text-xs text-slate-500"
          >
            {linkedInvoices.length}
          </span>
        </div>
        {linkedInvoices.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-500">
            No invoices linked to this vendor yet.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-medium text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200">
                <th className="px-4 py-2">Issued</th>
                <th className="px-4 py-2">Counterparty</th>
                <th className="px-4 py-2">Direction</th>
                <th className="px-4 py-2 text-right">Amount</th>
                <th className="px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {linkedInvoices.map((i) => (
                <tr
                  key={i.id}
                  data-testid={`vendor-detail-invoice-row-${i.id}`}
                  className="border-b last:border-b-0 border-slate-100 hover:bg-slate-50"
                >
                  <td className="px-4 py-2.5 text-slate-600">
                    {formatIsoDate(i.issuedOn)}
                  </td>
                  <td className="px-4 py-2.5 text-slate-900">
                    {i.counterparty}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-500 capitalize">
                    {i.direction}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums">
                    {formatAmountCents(i.amountCents, i.currency)}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-500 capitalize">
                    {i.status}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <Dialog open={mergeOpen} onOpenChange={setMergeOpen}>
        <DialogContent
          data-testid="vendor-merge-dialog"
          className="sm:max-w-md"
        >
          <DialogHeader>
            <DialogTitle>Merge &ldquo;{vendor.canonicalName}&rdquo; into…</DialogTitle>
            <DialogDescription>
              All linked expenses and invoices will be re-pointed at the target
              vendor, and this vendor row will be deleted. This cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <label
              htmlFor="vendor-merge-target"
              className="text-xs font-medium text-slate-700"
            >
              Target vendor
            </label>
            <select
              id="vendor-merge-target"
              data-testid="vendor-merge-target"
              value={mergeTargetId}
              onChange={(e) => setMergeTargetId(e.target.value)}
              className="px-3 py-2 text-sm rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#0070F2]/30 focus:border-[#0070F2]"
            >
              <option value="">Select a vendor…</option>
              {mergeCandidates.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.canonicalName}
                </option>
              ))}
            </select>
            {mergeError && (
              <div
                data-testid="vendor-merge-error"
                className="text-xs text-red-600"
              >
                {mergeError}
              </div>
            )}
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setMergeOpen(false)}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="vendor-merge-confirm"
              onClick={() => void runMerge()}
              disabled={merging || !mergeTargetId}
              className="px-4 py-2 text-sm font-medium text-white bg-[#0070F2] hover:bg-[#005BCA] disabled:bg-slate-300 disabled:cursor-not-allowed rounded-md transition-colors"
            >
              {merging ? "Merging…" : "Merge"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
